
import {FoundGameItem, GameItem, GameResponse} from '@/data/types';
import {timeout} from '@/util';
import {computed, defineComponent, nextTick, onMounted, ref, shallowRef, watch} from 'vue';
import {useRouter} from 'vue-router';

type State = 'Idle' | 'Loading' | 'Game' | 'Error';

export default defineComponent({
	setup() {
		const title = process.env.VUE_APP_TITLE;
		const router = useRouter();

		const state = ref<State>('Idle');
		const loading = ref(false);
		const username = ref<string | null>(null);
		const activeGameTitle = ref<string | null>(null);
		const message = ref<string>();
		const hubUrl = ref<string>();

		const contentBoxHeight = ref<string>();

		const items = ref<GameItem[]>([]);
		const found = computed(() => items.value.filter(i => i.found).length);
		const selectedItemNo = computed(() => router.currentRoute.value.params.itemId ? parseInt(router.currentRoute.value.params.itemId as string) : null);
		const selectedItem = shallowRef<FoundGameItem>();
		const selectedFoundAtDisplay = computed(() => {
			if (!selectedItem.value || !selectedItem.value.found) return '';
			return new Intl.DateTimeFormat([], {
				dateStyle: 'long',
				timeStyle: 'short',
			}).format(new Date(selectedItem.value.foundAt));
		});

		const checkItem = async () => {
			if (selectedItem.value?.found && !selectedItem.value.seen)
				selectedItem.value.seen = true;
			if (!selectedItemNo.value) return selectedItem.value = undefined;
			const item = items.value.find(i => i.id === selectedItemNo.value);
			if (!item || !item.found) return router.replace({name: 'home'});
			selectedItem.value = item;

			if (!item.seen) {
				// todo: animation and stuff

				const response = await fetch(`${process.env.VUE_APP_API_PATH}/games/shroom22/items/${item.id}/seen`, {
					method: 'post',
				});
				if (!response.ok) return;
				const json: { success: boolean } = await response.json();
				if (!json.success) return;
			}
		};

		onMounted(async () => {
			document.title = title;
			loading.value = true;
			state.value = 'Loading';

			const response = await fetch(`${process.env.VUE_APP_API_PATH}/games/shroom22`);

			if (!response.ok) {
				message.value = `Something went wrong loading your data. Please try again later. (${response.status} ${response.statusText})`;
				state.value = 'Error';
				loading.value = false;
				return;
			}

			message.value = undefined;
			const data: GameResponse = await response.json();
			loading.value = false;
			if (!data.user || data.noParticipant) return router.replace({name: 'register'});

			if (data.user.activeGame !== 'shroom22')
				activeGameTitle.value = data.user.activeGameTitle;

			hubUrl.value = data.hubUrl;
			username.value = data.user.name;
			items.value = data.items!;
			state.value = 'Game';

			return checkItem();
		});
		watch(selectedItemNo, checkItem);

		const preContentBoxTransition = async () => {
			contentBoxHeight.value = document.querySelector<HTMLElement>('.content-box')!.offsetHeight + 'px';
		};

		const doContentBoxTransition = async () => {
			await nextTick();
			const contentBox = document.querySelector<HTMLElement>('.content-box')!;
			const lastElement = Array.from(contentBox.querySelectorAll<HTMLElement>(':scope > :not(.slide-up-leave-active):not(.slide-up-enter-active)')).pop()!;
			const marginBottom = parseFloat(getComputedStyle(lastElement).marginBottom);
			const {bottom} = lastElement.getBoundingClientRect();
			const enteringElement = contentBox.querySelector<HTMLElement>(':scope > .slide-up-enter-active');
			const height = enteringElement?.getBoundingClientRect().height ?? 0;
			const marginTop = enteringElement ? parseFloat(getComputedStyle(enteringElement).marginTop) : 0;
			contentBoxHeight.value = `calc(${bottom + Math.max(marginBottom, marginTop) + height}px - 1rem)`;
			await Promise.all([
				new Promise<void>(resolve => contentBox.addEventListener('transitionend', () => resolve())),
				enteringElement && new Promise<void>(resolve => enteringElement.addEventListener('transitionend', () => resolve())),
			]);
			contentBoxHeight.value = undefined;
		};

		const switchGame = async () => {
			loading.value = true;

			const response = await fetch(`${process.env.VUE_APP_API_PATH}/switch-game`, {
				method: 'post',
				headers: {'content-type': 'application/json'},
				body: JSON.stringify({gameId: 'shroom22'}),
			});

			await preContentBoxTransition();
			loading.value = false;

			if (response.ok) {
				loading.value = false;
				message.value = 'All set!';
				activeGameTitle.value = null;
			} else {
				message.value = `Something went switching. Please try again later. (${response.status} ${response.statusText})`;
			}

			await doContentBoxTransition();
			await timeout(5000);

			await preContentBoxTransition();
			message.value = undefined;
			await doContentBoxTransition();
		};

		return {
			title, message, username,
			loading, state, contentBoxHeight,
			activeGameTitle, switchGame,
			items, found, hubUrl,
			selectedItem, selectedFoundAtDisplay,
			baseUrl: process.env.BASE_URL.replace(/\/$/, ''),
		};
	},
});
